import { useMemo } from "react";

import { onGetStoredViewListForWorkspace } from "@/domain/view/lib/on-get-stored-view-list-for-workspace.react-query";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { workspaceByIdQueryDocument } from "./workspace.gql";

export interface UseGetWorkspaceProps {
  id?: string;
  enabled?: boolean;
}

export const useWorkspaceById = ({ id, enabled = true }: UseGetWorkspaceProps) => {
  const queryClient = useQueryClient();

  const queryResult = useQuery({
    queryKey: queryKeys.workspaces.detail(id ?? ""),
    queryFn: async () => {
      if (!id) {
        throw new Error("Workspace ID is required");
      }
      const client = createOperationClient("workspaceById");
      const data = await client.request(workspaceByIdQueryDocument, { input: id });
      return data.workspaceById;
    },
    enabled: enabled && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes for individual items
  });

  const workspace = useMemo(() => {
    const result = queryResult.data ?? null;

    // Handle stored view list processing when data is available
    if (result?.storedViewList) {
      onGetStoredViewListForWorkspace({
        queryClient,
        workspaceId: result.id,
        storedViewList: result.storedViewList,
      });
    }

    return result;
  }, [queryResult.data, queryClient]);

  return {
    queryResult,
    workspace,
    loading: queryResult.isLoading,
    error: queryResult.error,
  };
};
